<template>
    <div class="col-lg-9">
        <div class="d-flex align-items-center mb-2">
            <h4 class="text-7">{{ eventTitle || 'Events' }}</h4>
            <div class="ml-auto">
                <!--
                  If we are NOT in summary view, show the Create Event button
                  if the user has permission.
                -->
                <router-link
                    v-if="!isSummaryView && hasPermission(permissions, 'create-events')"
                    :to="`/business/events-form`"
                    class="btn btn-primary btn-sm text-white shadow-none"
                >
                    <i class="fas fa-plus-circle mr-2"></i>Create Event
                </router-link>
                <!--
                  If we are in summary view and we have a current event, show a Go Live button.
                  The Go Live button calls handleGoLive().
                -->
                <button
                    v-else-if="isSummaryView && currentEvent"
                    class="btn btn-success btn-sm text-white shadow-none"
                    @click="handleGoLive"
                >
                    <i class="fas fa-podcast mr-2"></i>Go Live
                </button>
            </div>
        </div>

        <!-- Custom Success Alert -->
        <div v-if="successMessage" class="custom-alert alert-success" role="alert">
            <i class="fas fa-check-circle"></i>
            <span>{{ successMessage }}</span>
        </div>

        <!-- Custom Error Alert -->
        <div v-if="errorMessage" class="custom-alert alert-danger" role="alert">
            <i class="fas fa-exclamation-circle"></i>
            <span>{{ errorMessage }}</span>
        </div>

        <div class="custom-nav-container mb-3">
            <ul class="custom-nav-tabs">
                <li class="custom-nav-item">
                    <router-link class="custom-nav-link"
                                 :class="{ active: $route.path.startsWith('/business/events') && !$route.path.includes('/view') && !$route.path.includes('/report') }"
                                 :to="`/business/events`">
                        All Events
                    </router-link>
                </li>
                <li class="custom-nav-item">
                    <router-link class="custom-nav-link"
                                 :class="{ active: $route.path.includes('/view') }"
                                 :to="`/business/events/${$route.params.id || ''}/view`">
                        Event Summary
                    </router-link>
                </li>
                <li class="custom-nav-item">
                    <router-link class="custom-nav-link"
                                 :class="{ active: $route.path.includes('/report') }"
                                 :to="`/business/events/${$route.params.id || ''}/report`">
                        Ticket Sales
                    </router-link>
                </li>
            </ul>

            <div class="custom-nav-underline">
                <div class="custom-nav-active-underline"
                     :style="{ left: activeTabPosition, width: activeTabWidth }">
                </div>
            </div>
        </div>

        <keep-alive>
            <transition name="slide-in-left">
                <router-view></router-view>
            </transition>
        </keep-alive>
    </div>
</template>

<script>
import axios from "axios";

export default {
    name: "Index",
    props: ["permissions"],
    data() {
        return {
            activeTabPosition: "0px",
            activeTabWidth: "0px",
            eventTitle: "",
            errorMessage: "",
            successMessage: "", // Added property for success messages
            // Local storage for the current event coming from Summary.vue.
            currentEvent: null,
        };
    },
    computed: {
        isSummaryView() {
            return this.$route.path.includes("/view");
        },
    },
    mounted() {
        this.updateActiveTabPosition();
        // Listen for global events from Summary.vue.
        this.$root.$on("update-event-title", (title) => {
            this.eventTitle = title;
        });
        this.$root.$on("update-current-event", (eventData) => {
            this.currentEvent = eventData;
        });
    },
    methods: {
        updateActiveTabPosition() {
            this.$nextTick(() => {
                const activeTab = document.querySelector(".custom-nav-tabs .custom-nav-link.active");
                if (activeTab) {
                    this.activeTabPosition = `${activeTab.offsetLeft}px`;
                    this.activeTabWidth = `${activeTab.offsetWidth}px`;
                }
            });
        },
        handleGoLive() {
            // Clear any previous messages.
            this.errorMessage = "";
            this.successMessage = "";

            // Ensure we have a current event.
            if (!this.currentEvent || !this.currentEvent.id) {
                this.errorMessage = "Event data is not loaded yet. Please refresh the page.";
                setTimeout(() => {
                    this.errorMessage = "";
                }, 5000);
                return;
            }

            // If the event is already live, display a specific message.
            if (this.currentEvent.event_status === "LIVE-EVENT" || this.currentEvent.status === "ACTIVE") {
                this.errorMessage = "This event is already live.";
                setTimeout(() => {
                    this.errorMessage = "";
                }, 5000);
                return;
            }

            // For subsequent events the Go Live button should be available when the event status is TICKETS-CREATED.
            if (this.currentEvent.event_status !== "TICKETS-CREATED") {
                this.errorMessage = "This event is not approved yet. Please wait for admin approval.";
                setTimeout(() => {
                    this.errorMessage = "";
                }, 5000);
                return;
            }

            const eventId = this.currentEvent.id;
            console.log("Go Live for event id:", eventId);
            axios.post("/business/event-onboarding/go-live", {event_id: eventId})
                .then((response) => {
                    if (response.data.success) {
                        // Set a styled success message.
                        this.successMessage = response.data.message; // "Event is now live!"
                        setTimeout(() => {
                            this.successMessage = "";
                        }, 5000);
                        // Update the local event: now status becomes ACTIVE.
                        this.currentEvent.event_status = "ACTIVE";
                        this.$root.$emit("update-event-status", "ACTIVE");
                    } else {
                        this.errorMessage = response.data.message;
                        setTimeout(() => {
                            this.errorMessage = "";
                        }, 5000);
                    }
                })
                .catch((error) => {
                    console.error("Error in handleGoLive:", error);
                    this.errorMessage = "An error occurred. Please try again.";
                    setTimeout(() => {
                        this.errorMessage = "";
                    }, 5000);
                });
        },

        watch: {
            "$route.path": function () {
                this.updateActiveTabPosition();
            },
        },
    }
};
</script>

<style scoped>
.custom-alert {
    display: flex;
    align-items: center;
    padding: 1rem 1.25rem;
    margin-top: 1rem;
    border: 1px solid transparent;
    border-radius: 0.25rem;
    font-size: 0.95rem;
    animation: fadeIn 0.5s ease-in-out;
}
.custom-alert i {
    margin-right: 0.5rem;
    font-size: 1.2rem;
}
@keyframes fadeIn {
    from {
        opacity: 0;
        transform: translateY(-10px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}

/* Success Alert Styling */
.alert-success {
    background-color: #d4edda;
    border-color: #c3e6cb;
    color: #155724;
}

/* Error Alert Styling */
.alert-danger {
    background-color: #f8d7da;
    border-color: #f5c6cb;
    color: #721c24;
}
</style>
